import { apiV1 } from "api-v2";
import type { AxiosResponse } from "axios";
import type { InsuranceOcrJob } from "../types/InsuranceOcrJob.type";

export interface DocumentResource {
  id: number;
  name: string;
  category_id: number;
  company_id: number;
  created_at: string;
  extra_info: Record<string, unknown> | null;
  file_url: string;
  sub_category_id: number | null;
  updated_at: string;
}

export interface PostInsuranceDocumentResponse {
  /** Present when monthly verification cap was reached — document created without OCR */
  cap_reached?: boolean;
  document: DocumentResource;
  ocr_job?: InsuranceOcrJob;
  resets_at?: string;
}

export interface CapReachedError {
  code: "cap_reached";
  resets_at: string;
}

export interface InsuranceUsage {
  resets_at: string;
  uploads_limit: number;
  uploads_used: number;
}

export const postInsuranceDocument = (
  companyId: number,
  formData: FormData
): Promise<AxiosResponse<PostInsuranceDocumentResponse>> => {
  return apiV1.post<PostInsuranceDocumentResponse>(
    `companies/${companyId}/documents`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const replaceFile = (
  companyId: number,
  documentId: number,
  formData: FormData
): Promise<AxiosResponse<PostInsuranceDocumentResponse>> => {
  return apiV1.post<PostInsuranceDocumentResponse>(
    `companies/${companyId}/documents/${documentId}/replace-file`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export interface ValidateExistingResponse {
  ocr_job: InsuranceOcrJob;
}

export const validateExisting = (
  companyId: number,
  documentId: number
): Promise<AxiosResponse<ValidateExistingResponse>> => {
  return apiV1.post(`companies/${companyId}/documents/${documentId}/validate`);
};

export interface ConfirmVerificationPayload {
  overrides: Record<string, string>;
  values: Record<string, unknown>;
}

export const confirmVerificationWithValues = (
  companyId: number,
  documentId: number,
  payload: ConfirmVerificationPayload,
  signedParams?: { expires: string; signature: string; }
): Promise<AxiosResponse<{ document: DocumentResource }>> => {
  const queryString = signedParams
    ? `?signature=${encodeURIComponent(signedParams.signature)}&expires=${encodeURIComponent(signedParams.expires)}`
    : "";
  return apiV1.patch(
    `companies/${companyId}/documents/${documentId}/confirm-verification${queryString}`,
    payload
  );
};

export interface FeedbackPayload {
  comment?: string;
  rating: 0 | 1;
}

export interface SubmitFeedbackResponse {
  ocr_job: InsuranceOcrJob;
}

export const submitOcrJobFeedback = (
  companyId: number,
  ocrJobId: number,
  payload: FeedbackPayload
): Promise<AxiosResponse<SubmitFeedbackResponse>> => {
  return apiV1.post<SubmitFeedbackResponse>(
    `companies/${companyId}/ocr-jobs/${ocrJobId}/feedback`,
    payload
  );
};

export const getUsage = (
  companyId: number
): Promise<AxiosResponse<InsuranceUsage>> => {
  return apiV1.get<InsuranceUsage>(
    `companies/${companyId}/insurance-verification/usage`
  );
};

export interface DocumentWithDraft extends DocumentResource {
  is_draft: 0 | 1;
  latest_ocr_job: {
    id: number;
    created_at: string;
    error_message: string | null;
    state: import("../types/InsuranceOcrJob.type").InsuranceOcrJobState;
    sub_category: string | null;
  } | null;
  ocr_job: import("../types/InsuranceOcrJob.type").InsuranceOcrJob | null;
}

export const getDocument = (
  companyId: number,
  documentId: number
): Promise<AxiosResponse<{ data: DocumentWithDraft }>> => {
  return apiV1.get<{ data: DocumentWithDraft }>(
    `companies/${companyId}/documents/${documentId}`
  );
};

export const getDocuments = (
  companyId: number,
  includeDrafts = false
): Promise<AxiosResponse<{ data: DocumentWithDraft[] }>> => {
  const params = includeDrafts ? "?include_drafts=true" : "";
  return apiV1.get<{ data: DocumentWithDraft[] }>(
    `companies/${companyId}/documents${params}`
  );
};
